// src/pages/Payment.js

import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CheckoutForm from "../components/payments/CheckoutForm";
import axios from "../api/axios";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY, {
    stripeAccount: process.env.REACT_APP_STRIPE_ACCOUNT_ID,
  });

const invoice = {
  name: "Glow Healthcare Solutions LTD Invoice",
  type: "invoice",

  totalDue: 748,
  depositPaid: 325,
  price: 423,

  billTo: {
    name: "Glow Healthcare Solutions LTD",
    address: "63-67 St Peter's St, Nottingham NG7 3EN",
  },

  items: [
    {
      quantity: 34,
      description: "Website Section",
      unitPrice: 30,
      total: 1020,
    },
    {
      quantity: 1,
      description: "Glow Security Domain",
      unitPrice: null,
      total: 8.39,
    },
    {
      quantity: 1,
      description: "Glow Study Domain",
      unitPrice: 8.39,
      total: 8.39,
    },
    {
      quantity: 1,
      description: "Glow Group Domain",
      unitPrice: 8.39,
      total: 8.39,
    },
    {
      quantity: 1,
      description: "Glow Solutions Domain Transfer",
      unitPrice: null,
      total: 23.96,
    },
  ],

  subtotal: 1069.13,
  discount: 320,
};

function Payment() {
  const [clientSecret, setClientSecret] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [error, setError] = useState("");

  const initiatePayment = async () => {
    try {
      setLoadingPayment(true);
      setError("");
      setClientSecret(null);

      const response = await axios.post("/api/create-intent", {
        paymentMethodType: "card",
        currency: "gbp",
        amount: invoice.price * 100,
        packageName: invoice.name,
        type: invoice.type,
      });

      if (response.data.clientSecret) {
        console.log("Received client secret:", response.data.clientSecret);
        setClientSecret(response.data.clientSecret);
        setSessionId(response.data.sessionId);
      } else {
        throw new Error("Could not retrieve client secret from server");
      }
    } catch (err) {
      console.error("Error creating payment intent:", err);
      setError("Failed to initialize payment.");
    } finally {
      setLoadingPayment(false);
    }
  };

  useEffect(() => {
    initiatePayment();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#151934] to-[#b3ccf5]">
      <Navbar />

      <main className="mx-auto max-w-6xl px-4 pt-[120px] pb-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <section className="rounded-2xl bg-white p-6 shadow-xl">
            <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
              Invoice Payment
            </span>

            <h1 className="mt-4 text-3xl font-bold text-slate-900">
              Glow Healthcare Solutions LTD
            </h1>

            <p className="mt-2 text-slate-600">
              Review the invoice breakdown before completing payment.
            </p>

            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Bill To
              </p>
              <p className="mt-2 text-lg font-bold text-slate-900">
                {invoice.billTo.name}
              </p>
              <p className="text-slate-600">{invoice.billTo.address}</p>
            </div>

            <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="px-4 py-3">Qty</th>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3 text-right">Unit Price</th>
                    <th className="px-4 py-3 text-right">Total</th>
                  </tr>
                </thead>

                <tbody>
                  {invoice.items.map((item, index) => (
                    <tr key={index} className="border-b border-slate-100">
                      <td className="px-4 py-3 font-medium text-slate-700">
                        {item.quantity}
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {item.description}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700">
                        {item.unitPrice === null ? "N/A" : `£${item.unitPrice}`}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                        £{item.total}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal</span>
                <span>£{invoice.subtotal}</span>
              </div>

              <div className="flex justify-between text-green-600">
                <span>30% Discount</span>
                <span>-£{invoice.discount}</span>
              </div>

              <div className="flex justify-between text-slate-600">
                <span>Invoice Total</span>
                <span>£{invoice.totalDue}</span>
              </div>

              <div className="flex justify-between text-green-600">
                <span>Deposit Paid</span>
                <span>-£{invoice.depositPaid}</span>
              </div>

              <div className="border-t border-slate-200 pt-3">
                <div className="flex justify-between text-xl font-bold text-slate-900">
                  <span>Remaining Balance</span>
                  <span className="text-blue-600">£{invoice.price}</span>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-2xl bg-white p-6 shadow-xl lg:sticky lg:top-24 self-start">
            <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
              Secure Checkout
            </span>

            <h2 className="mt-4 text-2xl font-bold text-slate-900">
              Complete Payment
            </h2>

            <p className="mt-2 text-slate-600">
              Pay the invoice securely using Stripe.
            </p>

            {error && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                {error}
              </div>
            )}

            {loadingPayment && (
              <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50 p-4 font-medium text-blue-700">
                Preparing secure checkout...
              </div>
            )}

            {clientSecret && (
              <div className="mt-6">
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <CheckoutForm
                    amount={invoice.price}
                    sessionId={sessionId}
                  />
                </Elements>
              </div>
            )}

            <p className="mt-5 text-center text-xs text-slate-500">
              Secure payment powered by Stripe
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Payment;